import { useCallback } from 'react';
import { CONFIG } from '@frameio/config';
import { useIsDriveConnected } from '@frameio/connected-surfaces';
import { useGetProjectRootIdsQuery } from '@frameio/graphql-client/generated';
import { isSafari } from '@frameio/utils/browser';
import { isMobile } from '@frameio/vapor';
import { useActiveAccountId } from '../../../contexts';
import { useIsIntegrationClient } from '../../../contexts/ClientNameContext';
import { useOpenOnDesktopDialogContext } from '../../../contexts/ModalContext';
import { useOpenInBrowserMutation } from '../../../integrations/gql';
import { TOOLTIP_IDS } from '../../../onboarding/constants/tooltips';
import { tooltipStore } from '../../../onboarding/stores/tooltipStore';
import { useIsMountableStorageEnabled } from '../../../plans/hooks';
import { useTrackUnsafe } from '../../../segment/hooks';
import { MountedStorageStatusIcon } from '../MountedStorageStatusIcon';
import TEXT from './generated/TEXT/useOpenOnDesktopAction';
import { useMountedStorageStatus } from './useMountedStorageStatus';

type DriveAppUrlParams = {
  projectId?: string;
  accountId?: string;
  assetId?: string;
  returnTo?: string;
};

function getDriveAppUrl({
  projectId,
  accountId,
  assetId,
  returnTo,
}: DriveAppUrlParams): string {
  const params = new URLSearchParams();

  if (projectId) params.set('projectId', projectId);
  if (accountId) params.set('accountId', accountId);
  if (assetId) params.set('assetId', assetId);

  if (isSafari()) {
    if (returnTo) params.set('returnTo', returnTo);
    return `${CONFIG.universalLinkOrigin}/drive/open-asset?${params}`;
  }

  return `frameio-drive://open-asset?${params}`;
}

type UseOpenOnDesktopActionArgs = {
  /** The asset/folder to open. Omit to open the project's root folder instead. */
  assetId?: string | null;
  canOpenOnDesktop?: boolean;
  onPress?: () => void;
  projectId?: string;
  trackingSource: string;
};

TEXT.DEFINE({
  /** Menu item label for the action that opens the asset in the desktop app */
  openOnDesktop: 'Open on Desktop',
});

/**
 * Shared business logic behind every "Open on Desktop" button variant:
 * whether the button should render at all, whether it's disabled, the
 * reason it's blocked, and the press handler. The press deep-links into the
 * Drive app, which handles mounting and revealing the asset. The "Drive app
 * required" dialog is skipped while Drive is connected, since it's running.
 * The icon is badged with the project's Drive mount status.
 */
export function useOpenOnDesktopAction({
  assetId,
  canOpenOnDesktop = true,
  onPress,
  projectId,
  trackingSource,
}: UseOpenOnDesktopActionArgs) {
  const track = useTrackUnsafe();
  const accountId = useActiveAccountId();
  const isMountableStorageEnabled = useIsMountableStorageEnabled();

  const { open: openDialog } = useOpenOnDesktopDialogContext();
  const { shouldEnable, shouldHide, canMount, error } =
    useMountedStorageStatus(projectId);
  const [openInBrowser] = useOpenInBrowserMutation({
    context: { resolveWithUXP: true },
  });
  const isIntegrationClient = useIsIntegrationClient();
  const isDriveConnected = useIsDriveConnected();

  const { data: rootIdsData } = useGetProjectRootIdsQuery({
    variables: { projectId: projectId ?? '' },
    skip:
      !projectId ||
      !!assetId ||
      !isMountableStorageEnabled ||
      !canOpenOnDesktop ||
      shouldHide ||
      isMobile,
  });

  const targetAssetId = assetId ?? rootIdsData?.account?.project?.rootAssetId;

  const handlePress = useCallback(() => {
    if (!shouldEnable || !canMount || !targetAssetId) return;
    tooltipStore.dismissTooltip(TOOLTIP_IDS.MOUNT_PROJECT_BUTTON);
    track('open-on-desktop-clicked', {
      project_id: projectId,
      asset_id: targetAssetId,
      account_id: accountId,
      source: trackingSource,
    });
    onPress?.();

    const driveAppUrl = getDriveAppUrl({
      projectId,
      accountId,
      assetId: targetAssetId,
      returnTo: `${window.location.pathname}${window.location.search}`,
    });
    const openDriveRequiredDialog = () => {
      if (!isDriveConnected) openDialog({ projectId });
    };

    if (isIntegrationClient) {
      openInBrowser({
        variables: {
          input: {
            url: driveAppUrl,
          },
        },
      })
        .then((e) => {
          const didOpen = e?.data?.openInBrowser || false;
          if (!didOpen) openDriveRequiredDialog();
        })
        .catch(openDriveRequiredDialog);
    } else {
      window.location.href = driveAppUrl;
      if (!isSafari()) openDriveRequiredDialog();
    }
  }, [
    accountId,
    canMount,
    isDriveConnected,
    isIntegrationClient,
    onPress,
    openDialog,
    openInBrowser,
    projectId,
    shouldEnable,
    targetAssetId,
    track,
    trackingSource,
  ]);

  return {
    icon: <MountedStorageStatusIcon projectId={projectId} />,
    isHidden:
      isMobile ||
      !isMountableStorageEnabled ||
      !canOpenOnDesktop ||
      shouldHide ||
      !targetAssetId,
    isDisabled: !canMount,
    label: TEXT.openOnDesktop,
    error,
    onPress: handlePress,
  };
}
